import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Trash2, Spade, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, GamblingNotice } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";

/* Roulette TABLE overlay — designed to sit on top of the physical 3D roulette
   table (bird's-eye camera). The page is fully transparent: ghost betting cells
   align over the model's felt, and the physical wheel stays visible — we only
   project a small result chip onto the wheel zone. Calibration query params let
   devs align the layout to their camera/FOV:
   ?gx=42&gy=30&gw=40&gh=58  (betting grid zone, % of screen)
   &wx=6&wy=48&ww=28&wh=46   (wheel zone — result chip lands here)
   &cx=42&cy=90&cw=40        (chip strip zone)
   &hudx=84&hudy=4           (balance/stake HUD zone)
   &room=main|high           (table limits)
*/

const GOLD = "#d4af37";
const REDS = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const numColor = (n) => (n === 0 ? "#16a34a" : REDS.has(n) ? "#dc2626" : "#18181b");
const fmt = (n) => `$${(n ?? 0).toLocaleString()}`;
const ROOM_CHIPS = { main: [10, 25, 50, 100], high: [500, 1000, 2500, 5000] };

const pct = (v, d) => (v == null || v === "" || !Number.isFinite(Number(v)) ? d : Number(v));

export default function RouletteTablePage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const room = params.get("room") === "high" ? "high" : "main";
  // calibration zones (percent of screen) — defaults fill the frame; devs re-align
  // to their table camera with the query params when wrapping the 3D model
  const Z = {
    gx: pct(params.get("gx"), 26), gy: pct(params.get("gy"), 22), gw: pct(params.get("gw"), 56), gh: pct(params.get("gh"), 64),
    wx: pct(params.get("wx"), 4), wy: pct(params.get("wy"), 34), ww: pct(params.get("ww"), 20), wh: pct(params.get("wh"), 52),
    cx: pct(params.get("cx"), 26), cy: pct(params.get("cy"), 89), cw: pct(params.get("cw"), 56),
    hudx: pct(params.get("hudx"), 84), hudy: pct(params.get("hudy"), 4),
  };

  const [bank, setBank] = useState(demo ? 5000 : null);
  const [bets, setBets] = useState({});
  const [chip, setChip] = useState(ROOM_CHIPS[room][0]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const sfx = usePhoneAudio();
  const timers = useRef([]);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/casino/state`, { params: { token } })
      .then((r) => setBank(r.data.bank))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(load, [load]);

  const stake = Object.values(bets).reduce((a, b) => a + b, 0);
  const chips = ROOM_CHIPS[room];

  const placeBet = (key) => {
    if (spinning || busy) return;
    sfx.keyTick();
    setBets((b) => ({ ...b, [key]: (b[key] || 0) + chip }));
  };

  const evalDemo = (n) => {
    let payout = 0;
    for (const [key, amt] of Object.entries(bets)) {
      const win = (() => {
        if (key.startsWith("straight:")) return n === Number(key.split(":")[1]);
        if (key === "red") return REDS.has(n);
        if (key === "black") return n !== 0 && !REDS.has(n);
        if (key === "even") return n !== 0 && n % 2 === 0;
        if (key === "odd") return n % 2 === 1;
        if (key === "low") return n >= 1 && n <= 18;
        if (key === "high") return n >= 19;
        if (key.startsWith("dozen")) { const d = Number(key.slice(5)); return n >= (d - 1) * 12 + 1 && n <= d * 12; }
        if (key.startsWith("col")) { const c = Number(key.slice(3)); return n !== 0 && (n - c) % 3 === 0; }
        return false;
      })();
      if (win) payout += amt * (key.startsWith("straight:") ? 36 : key.startsWith("dozen") || key.startsWith("col") ? 3 : 2);
    }
    return payout;
  };

  const spin = async () => {
    if (spinning || busy || stake === 0) { if (stake === 0) toast.error("Place a bet first"); return; }
    if (stake > (bank ?? 0)) { toast.error("Not enough in the bank"); return; }
    sfx.click?.();
    setBusy(true); setSpinning(true); setResult(null);
    if (demo) {
      const n = Math.floor(Math.random() * 37);
      const payout = evalDemo(n);
      setBank((b) => b - stake + payout);
      later(() => {
        setSpinning(false);
        setResult({ result: n, payout, stake });
        setBets({});
        if (payout > 0) sfx.success();
        setBusy(false);
      }, 4200);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/casino/roulette/spin`, { bets, room }, { params: { token } });
      later(() => {
        setSpinning(false);
        setResult(r.data);
        setBets({});
        if (r.data.payout > 0) sfx.success();
        setBank((b) => (b ?? 0) - stake + r.data.payout);
        setBusy(false);
      }, 4200);
    } catch (e) {
      setSpinning(false); setBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  /* felt cell — solid casino colours, always readable, gold inset ring when bet on */
  const cell = "relative font-mono font-bold text-white transition-all duration-150 hover:brightness-125 active:scale-95 text-white/95";
  const chipTag = (key) => bets[key] ? (
    <span data-testid={`rt-chip-${key.replace(":", "-")}`} className="absolute -bottom-1 -right-1 min-w-4 h-4 px-0.5 rounded-full text-[8px] flex items-center justify-center font-mono font-bold z-10"
      style={{ backgroundColor: GOLD, color: "#000", boxShadow: "0 0 10px #d4af37aa" }}>{bets[key]}</span>
  ) : null;

  return (
    <div data-testid="roulette-table-page" className="fixed inset-0 overflow-hidden pointer-events-none" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="roulette table overlay" />}
      <GamblingNotice />

      {/* wheel zone — physical wheel stays visible; we project the result chip only */}
      <div className="absolute flex items-center justify-center" style={{ left: `${Z.wx}%`, top: `${Z.wy}%`, width: `${Z.ww}%`, height: `${Z.wh}%` }}>
        {spinning && (
          <div data-testid="rt-spinning" className="flex flex-col items-center gap-2" style={{ animation: "ncFadeUp 0.3s ease both" }}>
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: GOLD }} />
            <p className="font-mono text-[9px] uppercase tracking-[0.4em] text-white/70" style={{ textShadow: "0 1px 6px #000" }}>No more bets</p>
          </div>
        )}
        {result && !spinning && (
          <div data-testid="rt-result" className="flex flex-col items-center gap-2" style={{ animation: "ncFadeUp 0.4s ease both" }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center border-4"
              style={{ backgroundColor: numColor(result.result), borderColor: GOLD, boxShadow: "0 0 30px #000000cc, 0 0 18px #d4af3788" }}>
              <span className="font-black text-2xl text-white" style={{ textShadow: "0 1px 4px #000" }}>{result.result}</span>
            </div>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-sm"
              style={{ backgroundColor: "#000000cc", color: result.payout > 0 ? "#34d399" : "#ef4444" }}>
              {result.payout > 0 ? `+${fmt(result.payout)}` : `-${fmt(result.stake)}`}
            </p>
          </div>
        )}
      </div>

      {/* betting grid zone — real felt panel over the physical table. Semi-transparent
          edges let the 3D model show; cells are solid casino colours so the layout
          always reads as a table even before camera calibration. */}
      <div data-testid="rt-grid" className="absolute pointer-events-auto" style={{ left: `${Z.gx}%`, top: `${Z.gy}%`, width: `${Z.gw}%`, height: `${Z.gh}%` }}>
        <div className="w-full h-full rounded-xl p-[0.8vw] flex flex-col gap-[0.5vw]"
          style={{ background: "linear-gradient(160deg, rgba(9,61,34,0.92), rgba(6,42,24,0.94))", border: "2px solid #d4af3755", boxShadow: "0 18px 50px rgba(0,0,0,0.55), inset 0 0 60px rgba(0,0,0,0.35)" }}>
          {/* plaque */}
          <div className="flex items-center justify-between px-1 shrink-0" style={{ height: "8%" }}>
            <p className="font-mono uppercase tracking-[0.4em] font-bold" style={{ color: GOLD, fontSize: "clamp(8px, 0.7vw, 12px)", textShadow: "0 1px 4px #000" }}>Roulette</p>
            <p className="font-mono uppercase tracking-[0.25em] text-white/60" style={{ fontSize: "clamp(7px, 0.6vw, 10px)" }}>{room === "high" ? "High Limit · min $500" : "Main Floor · min $10"}</p>
          </div>
          <div className="flex gap-[0.4vw]" style={{ height: "56%" }}>
            <button data-testid="rt-cell-0" onClick={() => placeBet("straight:0")}
              className={`${cell} rounded-md`}
              style={{ width: "7.5%", backgroundColor: bets["straight:0"] ? "#22c55e" : "#15803d", fontSize: "clamp(12px, 1.3vw, 22px)" }}>
              0{chipTag("straight:0")}
            </button>
            <div className="flex-1 grid grid-cols-12" style={{ gap: "0.3vw" }}>
              {[3, 2, 1].map((rowOffset) => (
                Array.from({ length: 12 }, (_, col) => rowOffset + col * 3).map((n) => {
                  const key = `straight:${n}`;
                  const red = REDS.has(n);
                  return (
                    <button key={n} data-testid={`rt-cell-${n}`} onClick={() => placeBet(key)}
                      className={`${cell} rounded-sm`}
                      style={{
                        backgroundColor: bets[key] ? (red ? "#ef4444" : "#3f3f46") : red ? "#991b1b" : "#18181b",
                        boxShadow: bets[key] ? `inset 0 0 0 2px ${GOLD}` : "inset 0 0 0 1px rgba(255,255,255,0.14)",
                        fontSize: "clamp(10px, 1.05vw, 18px)",
                      }}>
                      {n}{chipTag(key)}
                    </button>
                  );
                })
              ))}
            </div>
            <div className="grid grid-rows-3" style={{ width: "7.5%", gap: "0.3vw" }}>
              {[["col3", "2:1"], ["col2", "2:1"], ["col1", "2:1"]].map(([k, l]) => (
                <button key={k} data-testid={`rt-outside-${k}`} onClick={() => placeBet(k)}
                  className={`${cell} rounded-sm`}
                  style={{ backgroundColor: bets[k] ? "#52525b" : "rgba(0,0,0,0.45)", color: GOLD, boxShadow: bets[k] ? `inset 0 0 0 2px ${GOLD}` : "inset 0 0 0 1px rgba(255,255,255,0.14)", fontSize: "clamp(8px, 0.8vw, 13px)" }}>
                  {l}{chipTag(k)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3" style={{ height: "16%", gap: "0.4vw", paddingLeft: "calc(7.5% + 0.4vw)", paddingRight: "calc(7.5% + 0.4vw)" }}>
            {[["dozen1", "1st 12"], ["dozen2", "2nd 12"], ["dozen3", "3rd 12"]].map(([k, l]) => (
              <button key={k} data-testid={`rt-outside-${k}`} onClick={() => placeBet(k)}
                className={`${cell} rounded-sm uppercase tracking-[0.2em]`}
                style={{ backgroundColor: bets[k] ? "#52525b" : "rgba(0,0,0,0.45)", boxShadow: bets[k] ? `inset 0 0 0 2px ${GOLD}` : "inset 0 0 0 1px rgba(255,255,255,0.14)", fontSize: "clamp(8px, 0.85vw, 14px)" }}>
                {l}{chipTag(k)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-6" style={{ height: "16%", gap: "0.4vw", paddingLeft: "calc(7.5% + 0.4vw)", paddingRight: "calc(7.5% + 0.4vw)" }}>
            {[["low", "1–18"], ["even", "Even"], ["red", "◆"], ["black", "◆"], ["odd", "Odd"], ["high", "19–36"]].map(([k, l]) => (
              <button key={k} data-testid={`rt-outside-${k}`} onClick={() => placeBet(k)}
                className={`${cell} rounded-sm uppercase tracking-[0.12em]`}
                style={{
                  backgroundColor: bets[k] ? (k === "red" ? "#ef4444" : "#52525b") : k === "red" ? "#991b1b" : "rgba(0,0,0,0.5)",
                  color: k === "black" ? "#d4d4d8" : "#fafafa",
                  boxShadow: bets[k] ? `inset 0 0 0 2px ${GOLD}` : "inset 0 0 0 1px rgba(255,255,255,0.14)",
                  fontSize: "clamp(8px, 0.85vw, 14px)",
                }}>
                {l}{chipTag(k)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* chip strip zone */}
      <div className="absolute pointer-events-auto flex items-center gap-[0.6vw]" style={{ left: `${Z.cx}%`, top: `${Z.cy}%`, width: `${Z.cw}%` }}>
        {chips.map((c) => (
          <button key={c} data-testid={`rt-chip-select-${c}`} onClick={() => { setChip(c); sfx.keyTick(); }}
            className="rounded-full border-4 border-dashed font-mono font-bold transition-transform hover:scale-110"
            style={{ width: "clamp(34px, 2.8vw, 52px)", height: "clamp(34px, 2.8vw, 52px)", fontSize: "clamp(9px, 0.7vw, 12px)",
                     borderColor: chip === c ? GOLD : "#ffffff55", backgroundColor: chip === c ? GOLD : "#000000cc", color: chip === c ? "#000" : "#fff", boxShadow: "0 2px 8px #00000088" }}>
            {c}
          </button>
        ))}
        <button data-testid="rt-clear" onClick={() => setBets({})} className="p-2 rounded-full border border-white/30 text-white/80 hover:text-red-400 hover:border-red-500/60 transition-colors" style={{ backgroundColor: "#000000aa" }}>
          <Trash2 className="w-4 h-4" />
        </button>
        <button data-testid="rt-spin" disabled={spinning || busy || stake === 0} onClick={spin}
          className="ml-auto px-[1.6vw] py-[0.8vw] font-mono uppercase tracking-[0.3em] font-bold text-black rounded-md disabled:opacity-40"
          style={{ backgroundColor: GOLD, boxShadow: `0 0 24px ${GOLD}66`, fontSize: "clamp(10px, 0.9vw, 15px)" }}>
          {spinning ? "…" : "Spin"}
        </button>
      </div>

      {/* HUD zone — balance + stake */}
      <div className="absolute pointer-events-none flex flex-col items-end gap-1" style={{ left: `${Z.hudx}%`, top: `${Z.hudy}%` }}>
        <p className="font-mono text-[0.8vw] font-bold px-3 py-1.5 rounded-sm flex items-center gap-2" style={{ backgroundColor: "#000000bb", color: GOLD, textShadow: "0 1px 4px #000" }}>
          <Spade className="w-3.5 h-3.5" /><span data-testid="rt-balance">{bank == null ? "…" : fmt(bank)}</span>
        </p>
        <p className="font-mono text-[0.65vw] uppercase tracking-[0.2em] px-3 py-1 rounded-sm" style={{ backgroundColor: "#00000099", color: "#e4e4e7" }}>
          Stake <span data-testid="rt-stake" className="font-bold text-white">{fmt(stake)}</span>
        </p>
      </div>

      <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translate(0, 0); } }`}</style>
    </div>
  );
}
